import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  DynamicUi,
  DynamicUiRefProps,
  FieldValues,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {createRef} from 'react';

import {isNotNil} from 'ramda';

import {
  useGetSchemaQuery,
  useSetMyCredentialMutation,
  useSetUserCredentialMutation,
} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {handleApiError} from '../../../utils/handleApiError';
import {parsedRendererProps} from '../../EntityCustomFields/utils/parsedRendererProps';
import {INTEGRATION_CODES} from '../constants/IntegrationCodes';

interface EditIntegrationFormProps {
  userId: string;
  integrationCode: INTEGRATION_CODES;
  authorizationProfileId: string;
  defaultValues: Record<string, unknown>;
  onAfterSubmit?: () => void;
}

export function EditIntegrationForm(props: EditIntegrationFormProps) {
  const dynamicUIRef = createRef<DynamicUiRefProps>();

  const {data: user} = useGetCurrentUserInfoQuery();
  const isCurrentUser = user?.id === props.userId;

  const schemaId = match(props.integrationCode)
    .with(INTEGRATION_CODES.MERCEDES_XENTRY, () => 'external-credential-mercedes_xentry_job')
    .with(INTEGRATION_CODES.MERCEDES_CLARA, () => 'external-credential-daimler_clara')
    .with(INTEGRATION_CODES.OTHER_SAG, () => 'external-credential-sag')
    .with(INTEGRATION_CODES.OTHER_TECRMI, () => 'external-credential-tecrmi')
    .with(INTEGRATION_CODES.MERCEDES_DAIMLER_XENTRY, () => 'external-credential-daimler_xentry_job')
    .exhaustive();

  const {
    data: schema,
    isLoading: isLoadingSchema,
    isError: isSchemaError,
  } = useGetSchemaQuery({id: schemaId});

  const [setUserCredential, {isLoading: isUpdatingPayload}] = useSetUserCredentialMutation();
  const [setMyCredential, {isLoading: isUpdatingMyPayload}] = useSetMyCredentialMutation();

  const isLoading = isUpdatingPayload || isUpdatingMyPayload;

  const handleSubmit = (data: FieldValues) => {
    const mutation = isCurrentUser ? setMyCredential : setUserCredential;

    mutation({
      credentialRequestBody: {
        authorizationProfileId: props.authorizationProfileId,
        parameters: Object.entries(data).map(([key, value]) => ({
          name: key,
          value,
        })),
      },
      integratedServiceType: props.integrationCode,
      userId: props.userId,
    })
      .unwrap()
      .then(() => {
        closeCurrentDialog();
      })
      .then(props.onAfterSubmit)
      .catch(handleApiError);
  };

  return (
    <VStack spacing={4}>
      <DataStatus isLoading={isLoadingSchema} isError={isSchemaError}>
        {isNotNil(schema) && (
          <DynamicUi
            schema={parsedRendererProps(schema.root)}
            ref={dynamicUIRef}
            defaultValues={props.defaultValues}
            onSubmit={handleSubmit}
          />
        )}
      </DataStatus>
      <ButtonGroup align="right">
        <Button
          variant="secondary"
          title={i18n.t('general.actions.close')}
          onClick={closeCurrentDialog}
        />
        <Button
          type="submit"
          onClick={() => dynamicUIRef.current?.submit()}
          isLoading={isLoading}
          title={i18n.t('general.actions.saveChanges')}
        />
      </ButtonGroup>
    </VStack>
  );
}
